import {Component, OnInit, ChangeDetectionStrategy} from '@angular/core';
import { NgClass, NgOptimizedImage } from '@angular/common';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {ProductPriority} from '../../../../../models/product-priority/product-priority';
import {ProductService} from '../../../../../services/product/product.service';
import {ProductPriorityService} from '../../../../../services/product-priority/product-priority.service';
import {IsGranted} from '../../../../../decorator/is-granted';
import {MetaTitle} from '../../../../../decorator/meta-title';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {Product} from '../../../../../models/product';
import {LoaderService} from '../../../../../services/loader/loader.service';
import {AuthService} from '../../../../../services/auth/auth.service';
import {ImageService} from '../../../../../services/image/image.service';
import {ProductPriorityProduct} from '../../../../../models/product-priority/product-priority-product';
import {SnackbarService} from '../../../../../services/snackbar/snackbar.service';

@Component({
  selector: 'app-product-priority-form',
  imports: [
    RouterLink,
    ReactiveFormsModule,
    NgOptimizedImage,
    NgClass
],
  templateUrl: './product-priority-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './product-priority-form.component.scss'
})
@IsGranted(['PRODUCT_PRIORITY_VIEW'])
@MetaTitle('Products priority list')
export class ProductPriorityFormComponent implements OnInit {
  loaded = false;
  productPriority: ProductPriority | null = null;
  products: Product[] = [];

  form = new FormGroup({
    name: new FormControl('', Validators.required),
    identifier: new FormControl('', Validators.required),
  });

  constructor(
    private productService: ProductService,
    private productPriorityService: ProductPriorityService,
    private route: ActivatedRoute,
    private loader: LoaderService,
    private auth: AuthService,
    private router: Router,
    private snackbar: SnackbarService,
    protected imageService: ImageService,
  ) {
  }

  ngOnInit(): void {
    this.productService.findAll({sort: 'name'}).subscribe(products => {
      this.products = products;

      const id = Number(this.route.snapshot.paramMap.get('id'));

      if (id) {
        this.loader.startLoading();
        this.productPriorityService.findOneById(id, {groups: 'product'}).subscribe((productPriority: ProductPriority) => {
          this.productPriority = productPriority;

          this.form.patchValue({
            name: this.productPriority.name,
            identifier: this.productPriority.identifier,
          });
          this.loader.stopLoading();
          this.loaded = true;
        });
      } else {
        this.productPriority = new ProductPriority();
        this.loaded = true;
      }
    });
  }

  isCreate(): boolean {
    return !this.productPriority?.id;
  }

  canUpdate(): boolean {
    return this.auth.hasRolesSync(['ROLE_API_PRODUCT_PRIORITY_WRITE']);
  }

  getOrderedProducts(): ProductPriorityProduct[] {
    return this.productPriority?.products ?? [];
  }

  up(productPriorityProduct: ProductPriorityProduct): void {
    const i = this.productPriority?.products.indexOf(productPriorityProduct);
    if (i !== undefined && i > 0) {
      this.productPriority?.products.splice(i, 1);
      this.productPriority?.products.splice(i - 1, 0, productPriorityProduct);
      this.generatePosition();
    }
  }

  down(productPriorityProduct: ProductPriorityProduct): void {
    if (this.productPriority?.products) {
      const i = this.productPriority.products.indexOf(productPriorityProduct);
      if (i !== undefined && i < this.productPriority.products.length - 1) {
        this.productPriority?.products.splice(i, 1);
        this.productPriority?.products.splice(i + 1, 0, productPriorityProduct);
        this.generatePosition();
      }
    }
  }

  remove(productPriorityProduct: ProductPriorityProduct): void {
    if (this.productPriority?.products) {
      const i = this.productPriority.products.indexOf(productPriorityProduct);
      if (i !== undefined) {
        this.productPriority?.products.splice(i, 1);
        this.generatePosition();
      }
    }
  }

  add(product: Product): void {
    if (this.productPriority?.products) {
      let productPriorityProduct = new ProductPriorityProduct();
      productPriorityProduct.product = product;
      this.productPriority.products.push(productPriorityProduct);
      this.generatePosition();
    }
  }

  getAvailableProducts(): Product[] {
    let availableProducts: Product[] = [];

    if (this.productPriority?.products) {
      this.products.forEach(product => {
        const f = this.productPriority?.products.find(p => p.product?.id === product.id);
        if (f === undefined) {
          availableProducts.push(product);
        }
      });
    }

    return availableProducts;
  }

  generatePosition(): void {
    let position = 0;
    this.productPriority?.products.forEach(productPriorityProduct => {
      productPriorityProduct.position = position++;
    });
  }

  onSubmit(): void {
    if (!this.form.valid) {
      return;
    }

    this.loader.startLoading();

    let params: any = this.form.value;

    if (this.productPriority?.products) {
      params.products = this.productPriority?.products.map(productPriority => productPriority?.product?.id);
    }

    if (this.isCreate()) {
      this.productPriorityService.create(params).subscribe(() => {
        this.loader.stopLoading();
        this.snackbar.success('Product priority list has been created.');
        this.router.navigateByUrl('/configuration/products-priority').then();
      });
    } else if (this.productPriority?.id) {
      this.productPriorityService.update(this.productPriority.id, params).subscribe(() => {
        this.loader.stopLoading();
        this.snackbar.success('Product priority list has been modified.');
        this.router.navigateByUrl('/configuration/products-priority').then();
      });
    }
  }

  nameChanged(): void {
    const formIdentifier = this.form.get('identifier');
    if (!formIdentifier?.value) {
      let identifier = this.form.get('name')?.value ?? '';

      identifier = identifier.toLowerCase()
        .replaceAll(/[^a-z0-9-]/g, ' ')
        .trim()
        .replaceAll(/\s+/g, '-');

      formIdentifier?.setValue(identifier);
    }
  }
}
